import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import PageTabs from '@/components/shared/PageTabs';
import { TimelineFeed } from '@/components/shared/TimelineFeed';
import { TimelineItem } from '@/components/shared/TimelineFeed/TimelineItem';
import { LinkValue } from '@/components/shared/ValueRenderers';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { useTranslator } from '@/hooks/useTranslator';
import { AssessmentAllMitigationsSheet } from '../../../../../../-components/AssessmentAllMitigationsSheet';
import { AssessmentRiskFactorsTable } from '../../../../../../-components/AssessmentRiskFactorsTable';
import { AssessmentRiskPrioritizationTab } from '../../../../../../-components/AssessmentRiskPrioritizationTab';
import { CompanyAssessmentDetailHeader } from '../../../../../../-components/CompanyAssessmentDetailHeader';
import { FulfilledRulesSheet } from '../../../../../../-components/FulfilledRulesSheet';
import { ManualMitigationDialog } from '../../../../../../-components/ManualMitigationDialog';
import { RemarksSheet } from '../../../../../../-components/RemarksSheet';
import {
  ASSESSMENT_STATUS_LABEL_KEY,
  ASSESSMENT_STATUS_VARIANT,
} from '../../../../../../-constants/assessmentStatus';
import { useAssessmentHistory } from '../../../../../../-hooks/useAssessmentHistory';
import { useAssessmentRiskFactors } from '../../../../../../-hooks/useAssessmentRiskFactors';
import { useCompanyAssessment } from '../../../../../../-hooks/useCompanyAssessment';
import { useCompanyAssessmentPrioritization } from '../../../../../../-hooks/useCompanyAssessmentPrioritization';
import {
  toAssetNames,
  toTriggerAssets,
} from '../../../../../../-utils/assessmentTrigger';

function statusLabel(status: string, t: (key: string) => string): string {
  const key = ASSESSMENT_STATUS_LABEL_KEY[status];
  return key ? t(key) : status;
}

function formatTriggerEvent(
  event: string | null,
  t: (k: string) => string,
): string {
  const key = `history.trigger.${event ?? 'INITIAL_CALCULATION'}`;
  const translated = t(key);
  // i18next returns the key when translation is missing — use formatted fallback
  if (translated === key) {
    const raw = event ?? 'INITIAL_CALCULATION';
    return raw
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  }
  return translated;
}

export const Route = createFileRoute(
  '/(client)/risk/(pages)/(detail)/assessments/$id/assessed/$assessmentId/',
)({
  validateSearch: z.object({ tab: z.string().optional() }),
  component: CompanyAssessmentDetailPage,
});

function CompanyAssessmentDetailPage() {
  const { id: blueprintId, assessmentId } = Route.useParams();
  const { tab } = Route.useSearch() as { tab?: string };
  const { getTranslation: t } = useTranslator({ page: 'riskAssessments' });

  const [remarksRisk, setRemarksRisk] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [allMitigationsOpen, setAllMitigationsOpen] = useState(false);
  const [fulfilledRulesTarget, setFulfilledRulesTarget] = useState<{
    triggerEvent?: string;
    assets?: string[];
    riskId?: string;
    riskTitle?: string;
  } | null>(null);
  const [mitigationTarget, setMitigationTarget] = useState<{
    risk: { id: string; title: string };
    metricId: 'LIKELIHOOD' | 'SEVERITY';
    currentScore: number | null;
  } | null>(null);

  const assessment = useCompanyAssessment(assessmentId);
  const riskFactors = useAssessmentRiskFactors(assessmentId);
  const history = useAssessmentHistory(assessmentId);
  const prioritization = useCompanyAssessmentPrioritization(assessmentId);

  const activeTab = tab ?? 'assessment';
  const d = assessment.data;

  if (assessment.isLoading) {
    return (
      <PageContainer className='gap-4'>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className='h-32 w-full' />
        ))}
      </PageContainer>
    );
  }

  const companyName = d?.companyData?.display_name?.value ?? '—';
  const apv = d?.companyData?.annual_purchase_volume;
  const notSet = t('companyDetail.notSet');
  const apvDisplay = apv?.value
    ? `${apv.value}${apv.unit ? ` ${apv.unit}` : ''}${apv.year ? ` (${apv.year})` : ''}`
    : notSet;
  const revenueDisplay = d?.companyData?.annual_revenue?.value ?? notSet;

  return (
    <div className='flex flex-col h-full overflow-hidden'>
      <CompanyAssessmentDetailHeader
        blueprintId={blueprintId}
        companyName={companyName}
        blueprintName={d?.blueprintName ?? null}
        annualPurchaseVolume={apvDisplay}
        annualRevenue={revenueDisplay}
        scoreFormula={d?.scoreFormula ?? null}
        isArchived={!!d?.isBlueprintArchived}
      />

      <PageTabs
        tabs={[
          { key: 'assessment', label: t('detail.tabs.assessment') },
          { key: 'prioritization', label: t('detail.tabs.prioritization') },
          { key: 'history', label: t('detail.tabs.history') },
        ]}
        defaultTab='assessment'
        param='tab'
        moreLabel={t('detail.tabs.more')}
      />

      <div className='flex-1 min-h-0 overflow-hidden'>
        {activeTab === 'assessment' && (
          <PageContainer className='h-full overflow-hidden'>
            <AssessmentRiskFactorsTable
              rows={riskFactors.rows}
              total={riskFactors.total}
              isLoading={riskFactors.isLoading}
              sorting={riskFactors.sorting}
              filters={riskFactors.filters}
              pagination={riskFactors.pagination}
              onSortingChange={riskFactors.onSortingChange}
              onFiltersChange={riskFactors.onFiltersChange}
              onPaginationChange={riskFactors.onPaginationChange}
              assessmentId={assessmentId}
              onRemarks={risk => setRemarksRisk(risk)}
              onOpenFulfilledRules={risk =>
                setFulfilledRulesTarget({
                  riskId: risk.id,
                  riskTitle: risk.title,
                })
              }
              onEditMetric={(risk, metricId, currentScore) =>
                setMitigationTarget({ risk, metricId, currentScore })
              }
            />
          </PageContainer>
        )}

        {activeTab === 'prioritization' && (
          <PageContainer className='h-full overflow-y-auto'>
            <AssessmentRiskPrioritizationTab
              items={prioritization.data?.data ?? []}
              isLoading={prioritization.isLoading}
            />
          </PageContainer>
        )}

        {activeTab === 'history' && (
          <PageContainer className='h-full overflow-y-auto'>
            <TimelineFeed
              events={history.data?.pages.flatMap(p => p.data) ?? []}
              getEventId={e => e.id}
              getEventDate={e => e.createdAt}
              isLoading={history.isLoading}
              hasNextPage={history.hasNextPage}
              isFetchingNextPage={history.isFetchingNextPage}
              onLoadMore={() => void history.fetchNextPage()}
              renderEvent={event => (
                <TimelineItem
                  key={event.id}
                  createdAt={event.createdAt}
                  content={
                    <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                      <StatusBadge
                        label={statusLabel(event.status, t)}
                        variant={
                          ASSESSMENT_STATUS_VARIANT[event.status] ?? 'muted'
                        }
                      />
                      {event.likelihoodScore != null && (
                        <span className='text-xs text-muted-foreground'>
                          {t('history.columns.likelihood')}:{' '}
                          <strong className='text-foreground'>
                            {event.likelihoodScore.toFixed(2)}
                          </strong>
                        </span>
                      )}
                      {event.severityScore != null && (
                        <span className='text-xs text-muted-foreground'>
                          {t('history.columns.severity')}:{' '}
                          <strong className='text-foreground'>
                            {event.severityScore.toFixed(2)}
                          </strong>
                        </span>
                      )}
                      {event.averagePriorityScore != null && (
                        <span className='text-xs text-muted-foreground'>
                          {t('history.columns.score')}:{' '}
                          <strong className='text-foreground'>
                            {event.averagePriorityScore.toFixed(2)}
                          </strong>
                        </span>
                      )}
                      <span className='text-xs text-muted-foreground'>
                        {formatTriggerEvent(
                          event.assessmentTrigger?.event ?? null,
                          t,
                        )}
                      </span>
                      {/* LinkValue renders nothing when there are no asset
                          names, so the emptiness check lives there. */}
                      <LinkValue
                        value={toAssetNames(
                          event.assessmentTrigger?.assetName,
                        ).join(', ')}
                        onClick={() => {
                          const trigger = event.assessmentTrigger;
                          if (!trigger?.event) return;
                          setFulfilledRulesTarget({
                            triggerEvent: trigger.event,
                            // Ids, not the names rendered here — the old
                            // dashboard queries matched rules the same way.
                            assets: toTriggerAssets(trigger.asset),
                          });
                        }}
                      />
                      {event.hasMitigation && (
                        <button
                          type='button'
                          onClick={() => setAllMitigationsOpen(true)}
                          className='text-xs text-primary underline underline-offset-2 hover:text-primary/80 transition-colors'
                        >
                          {t('history.viewMitigations')}
                        </button>
                      )}
                    </div>
                  }
                />
              )}
            />
          </PageContainer>
        )}
      </div>

      <RemarksSheet
        open={!!remarksRisk}
        onClose={() => setRemarksRisk(null)}
        assessmentId={assessmentId}
        riskId={remarksRisk?.id ?? ''}
        riskTitle={remarksRisk?.title ?? ''}
      />

      <ManualMitigationDialog
        open={!!mitigationTarget}
        onClose={() => setMitigationTarget(null)}
        assessmentId={assessmentId}
        companyId={d?.companyId ?? ''}
        riskId={mitigationTarget?.risk.id ?? ''}
        metricId={mitigationTarget?.metricId ?? 'LIKELIHOOD'}
      />

      <FulfilledRulesSheet
        open={!!fulfilledRulesTarget}
        onClose={() => setFulfilledRulesTarget(null)}
        assessmentId={assessmentId}
        companyId={d?.companyId ?? ''}
        riskId={fulfilledRulesTarget?.riskId ?? null}
        riskTitle={fulfilledRulesTarget?.riskTitle ?? null}
        triggerEvent={fulfilledRulesTarget?.triggerEvent ?? null}
        assets={fulfilledRulesTarget?.assets ?? []}
      />

      <AssessmentAllMitigationsSheet
        open={allMitigationsOpen}
        onClose={() => setAllMitigationsOpen(false)}
        assessmentId={assessmentId}
      />
    </div>
  );
}
